import React, { Component, ErrorInfo, ReactNode } from 'react';
import { clearFirestoreCacheAndReload } from '../../firebase';

// A wedged local Firestore cache (see clearFirestoreCacheAndReload's comment) surfaces
// as this specific SDK-internal error text — distinct from an ordinary app bug, and a
// plain reload does not fix it since the corruption lives in this device's IndexedDB.
const isFirestoreInternalError = (error: Error | null) =>
    !!error?.message?.includes('INTERNAL ASSERTION FAILED');

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
    readonly props: Readonly<Props>;

    constructor(props: Props) {
        super(props);
        this.props = props;
    }

    public state: State = {
        hasError: false,
        error: null,
    };

    public static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('Uncaught error:', error, errorInfo);
    }

    public render() {
        if (this.state.hasError) {
            if (isFirestoreInternalError(this.state.error)) {
                return (
                    <div className="p-6 bg-red-900/20 border border-red-500 rounded-lg text-center text-white m-4">
                        <h2 className="text-xl font-bold mb-2 text-red-400">Local data cache needs to reset</h2>
                        <p className="mb-4 text-slate-300">
                            Your device's offline cache for this app got into a bad state. Clearing it fixes this —
                            you won't lose any saved data, this only clears a local copy.
                        </p>
                        <button
                            onClick={() => { clearFirestoreCacheAndReload(); }}
                            className="px-4 py-2 bg-red-600 hover:bg-red-500 rounded text-white font-bold transition-colors"
                        >
                            Clear Cache &amp; Reload
                        </button>
                    </div>
                );
            }
            return this.props.fallback || (
                <div className="p-6 bg-red-900/20 border border-red-500 rounded-lg text-center text-white m-4">
                    <h2 className="text-xl font-bold mb-2 text-red-400">Something went wrong</h2>
                    <p className="mb-4 text-slate-300">We encountered an error while loading this section.</p>
                    <div className="bg-slate-900 p-4 rounded text-left overflow-auto max-h-40 mb-4 border border-slate-700">
                        <code className="text-xs text-red-300 font-mono">
                            {this.state.error?.toString()}
                        </code>
                    </div>
                    <button
                        onClick={() => window.location.reload()}
                        className="px-4 py-2 bg-red-600 hover:bg-red-500 rounded text-white font-bold transition-colors"
                    >
                        Reload Page
                    </button>
                </div>
            );
        }

        return this.props.children;
    }
}
